import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { fetchBreedings } from "../../../api/breeding";
import { Button } from "../../common/Button";
import { BREEDER_BREEDINGS_PATH } from "../breeding/farmer/breedingRoutes";
import { setupBreedingPath, setupDevicesPath } from "./setupRoutes";
import { SetupLayout } from "./SetupLayout";
import { isSetupResolved, markSetupResolved } from "./setupStorage";
import { useBreedingBuildings } from "./useBreedingBuildings";

// Clés passées en props à SetupLayout, invisibles du parser sans ces marqueurs.
// i18n.t("setup-summary-title")
export const SetupSummaryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breedingId = Number(useParams().breedingId);

  const { buildings, loading } = useBreedingBuildings(breedingId);
  const [breedingName, setBreedingName] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchBreedings().then((result) => {
      const found = result.breedings.find((entry) => entry.id === breedingId);
      if (!cancelled && found) setBreedingName(found.name);
    });
    return () => {
      cancelled = true;
    };
  }, [breedingId]);

  // `Building.device` porte les produits installés : pas besoin d'interroger les produits
  // pour savoir si l'élevage remontera des mesures.
  const deviceCount = buildings.reduce(
    (total, building) => total + building.device.length,
    0
  );

  // Comme la sortie de SetupLayout : le premier parcours mène au tableau de bord, un
  // élevage créé ensuite depuis la page Élevages y ramène.
  const isFirstStart = !isSetupResolved();

  const finish = () => {
    markSetupResolved();
    navigate(isFirstStart ? "/breeder" : BREEDER_BREEDINGS_PATH, {
      replace: true,
    });
  };

  return (
    <SetupLayout
      step={4}
      breedingId={breedingId}
      titleKey="setup-summary-title"
    >
      <div className="bg-grey-light/50 rounded-lg p-5 text-default-text">
        <p className="font-oxaniumMedium text-lg">{breedingName}</p>

        {loading ? (
          <p className="mt-2 text-sm">{t("loading")}</p>
        ) : (
          <ul className="mt-4 flex flex-col gap-2">
            {buildings.map((building) => (
              <li
                key={building.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1"
              >
                <FontAwesomeIcon icon="building" />
                <span className="font-oxaniumMedium">{building.name}</span>
                <span className="text-sm text-grey-readable">
                  {building.device.length > 0
                    ? t("setup-summary-device-count", {
                        n: building.device.length,
                      })
                    : t("setup-summary-device-none")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!loading && deviceCount === 0 && (
        <div className="mt-4 p-4 rounded-lg bg-grey-light/50 text-default-text text-sm">
          <p className="mb-3">{t("setup-summary-no-device")}</p>
          <Button
            variant="secondary"
            onClick={() => navigate(setupDevicesPath(breedingId))}
          >
            {t("setup-summary-add-device")}
          </Button>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-3">
        <Button size="full" onClick={finish}>
          {isFirstStart
            ? t("setup-summary-finish")
            : t("setup-summary-back-to-breedings")}
        </Button>
        <Button
          variant="secondary"
          size="full"
          onClick={() => navigate(setupBreedingPath())}
        >
          {t("setup-summary-another")}
        </Button>
      </div>
    </SetupLayout>
  );
};
